import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { format, subDays } from 'date-fns'
import { auditService } from '@/services/auditService'

interface AuditLog {
  id: string
  timestamp: string
  user: string
  action: string
  entityType: string
  entityId: string
  changes?: Record<string, { old: any; new: any }>
  ipAddress?: string
  userAgent?: string
  metadata?: Record<string, any>
}

const AuditTrail = () => {
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    user: '',
    action: '',
    entityType: '',
    startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    search: '',
  })
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadAuditLogs()
  }, [filters])

  const loadAuditLogs = async () => {
    setLoading(true)
    try {
      const data = await auditService.fetchAuditLogs(filters)
      setLogs(data)
    } catch (error) {
      console.error('Failed to load audit logs', error)
      setLogs([])
    } finally {
      setLoading(false)
    }
  }

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (filters.user && !log.user.toLowerCase().includes(filters.user.toLowerCase())) return false
      if (filters.action && log.action !== filters.action) return false
      if (filters.entityType && log.entityType !== filters.entityType) return false
      if (filters.search) {
        const searchLower = filters.search.toLowerCase()
        if (
          !log.user.toLowerCase().includes(searchLower) &&
          !log.action.toLowerCase().includes(searchLower) &&
          !log.entityType.toLowerCase().includes(searchLower) &&
          !log.entityId.toLowerCase().includes(searchLower)
        ) {
          return false
        }
      }
      return true
    })
  }, [logs, filters])

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const getActionColor = (action: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (action) {
      case 'CREATE':
      case 'APPROVE':
        return 'success'
      case 'DELETE':
      case 'REJECT':
        return 'error'
      case 'UPDATE':
        return 'warning'
      case 'LOGIN':
      case 'LOGOUT':
        return 'info'
      default:
        return 'default'
    }
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Audit Trail
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Complete activity log of all system actions and changes
      </Typography>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="User"
                  value={filters.user}
                  onChange={(e) => setFilters({ ...filters, user: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={filters.action}
                    label="Action"
                    onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="CREATE">Create</MenuItem>
                    <MenuItem value="UPDATE">Update</MenuItem>
                    <MenuItem value="DELETE">Delete</MenuItem>
                    <MenuItem value="APPROVE">Approve</MenuItem>
                    <MenuItem value="REJECT">Reject</MenuItem>
                    <MenuItem value="LOGIN">Login</MenuItem>
                    <MenuItem value="LOGOUT">Logout</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Entity Type</InputLabel>
                  <Select
                    value={filters.entityType}
                    label="Entity Type"
                    onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Transaction">Transaction</MenuItem>
                    <MenuItem value="User">User</MenuItem>
                    <MenuItem value="Configuration">Configuration</MenuItem>
                    <MenuItem value="Channel">Channel</MenuItem>
                    <MenuItem value="Status">Status</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <DatePicker
                  label="Start Date"
                  value={filters.startDate ? new Date(filters.startDate) : null}
                  onChange={(date) =>
                    setFilters({ ...filters, startDate: date ? format(date, 'yyyy-MM-dd') : '' })
                  }
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      </LocalizationProvider>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell>Timestamp</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Entity Type</TableCell>
                <TableCell>Entity ID</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.map((log) => (
                <>
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleRow(log.id)}>
                        <ExpandMoreIcon
                          sx={{
                            transform: expandedRows.has(log.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.user}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={log.action} size="small" color={getActionColor(log.action)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.entityType}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {log.entityId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {log.ipAddress || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.changes && (
                        <Chip label={`${Object.keys(log.changes).length} change(s)`} size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedRows.has(log.id) && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0 }}>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Stack spacing={2}>
                            {log.changes && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Changes:
                                </Typography>
                                {Object.entries(log.changes).map(([key, change]) => (
                                  <Box key={key} sx={{ mb: 1 }}>
                                    <Typography variant="body2">
                                      <strong>{key}:</strong> {JSON.stringify(change.old)} → {JSON.stringify(change.new)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                            {log.metadata && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  Metadata:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  component="pre"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    bgcolor: 'background.paper',
                                    p: 1,
                                    borderRadius: 1,
                                    overflow: 'auto',
                                  }}
                                >
                                  {JSON.stringify(log.metadata, null, 2)}
                                </Typography>
                              </Box>
                            )}
                            {log.userAgent && (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  User Agent:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                  {log.userAgent}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  )
}

export default AuditTrail

